/**
 * Tab Navigation
 * Handles keyboard navigation, browser navigation, and tab activation
 */

class TabNavigation {
    constructor(stateManager, eventTrigger) {
        this.state = stateManager;
        this.triggerEvent = eventTrigger;
    }
    
    /**
     * Handle keyboard navigation
     */
    handleKeyboardNavigation(event) {
        try {
            if (event.ctrlKey && event.key === 'Tab') {
                event.preventDefault();
                this.navigateToNextTab();
                return;
            }
            
            if (event.ctrlKey && event.shiftKey && event.key === 'Tab') {
                event.preventDefault();
                this.navigateToPreviousTab();
                return;
            }
            
            if (event.ctrlKey && /^[1-9]$/.test(event.key)) {
                event.preventDefault();
                const tabIndex = parseInt(event.key) - 1;
                this.navigateToTabByIndex(tabIndex);
                return;
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Keyboard navigation error:', error);
            }
        }
    }
    
    /**
     * Handle browser popstate
     * NOTE: Browser history handler takes full control of popstate events
     * This method is kept for compatibility but does nothing - browser-history-handler.js handles all navigation
     */
    handlePopState(event) {
        // Browser history handler manages all popstate events
        // Tab switching is handled by browser-history-handler.js during state restoration
        return;
    }
    
    /**
     * Handle hash change
     */
    handleHashChange(event) {
        try {
            const newHash = window.location.hash.substring(1);
            const oldHash = event.oldURL ? new URL(event.oldURL).hash.substring(1) : '';
            
            if (newHash !== oldHash) {
                this.activateTabByLink(newHash);
            }
            
            if (window.HyperLog) {
                window.HyperLog.debug('[TabNavigation] Hash change handled:', { 
                    oldHash, 
                    newHash 
                });
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Hash change handler error:', error);
            }
        }
    }
    
    /**
     * Activate tab by link
     */
    activateTabByLink(tabLink) {
        try {
            const tabElement = document.querySelector(`#nav-tab button[tab-link="${tabLink}"]`);
            
            if (tabElement) {
                const tab = new bootstrap.Tab(tabElement);
                tab.show();
                return true;
            }
            
            return false;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Failed to activate tab by link:', error);
            }
            return false;
        }
    }
    
    /**
     * Navigate to next tab
     */
    navigateToNextTab() {
        try {
            const activeTab = document.querySelector('#nav-tab button.active');
            if (!activeTab) return;
            
            const nextTab = activeTab.parentElement.nextElementSibling?.querySelector('button');
            if (nextTab) {
                const tab = new bootstrap.Tab(nextTab);
                tab.show();
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Navigate to next tab error:', error);
            }
        }
    }
    
    /**
     * Navigate to previous tab
     */
    navigateToPreviousTab() {
        try {
            const activeTab = document.querySelector('#nav-tab button.active');
            if (!activeTab) return;
            
            const prevTab = activeTab.parentElement.previousElementSibling?.querySelector('button');
            if (prevTab) {
                const tab = new bootstrap.Tab(prevTab);
                tab.show();
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Navigate to previous tab error:', error);
            }
        }
    }
    
    /**
     * Navigate to tab by index
     */
    navigateToTabByIndex(index) {
        try {
            const tabs = document.querySelectorAll('#nav-tab button');
            if (index >= 0 && index < tabs.length) {
                const tab = new bootstrap.Tab(tabs[index]);
                tab.show();
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Navigate to tab by index error:', error);
            }
        }
    }
    
    /**
     * Update URL hash
     */
    updateUrlHash(hash) {
        try {
            // Update URL hash through debounced mechanism (2 seconds) instead of immediately
            // This prevents immediate hash updates when user clicks on tabs
            const desiredHash = hash.startsWith('#') ? hash : `#${hash}`;
            
            // Use filterController's updateUrlWithFilters for debounced hash update
            if (window.filterController && typeof window.filterController.updateUrlWithFilters === 'function') {
                // Update hash through debounce mechanism (2 seconds)
                window.filterController.updateUrlWithFilters(true, desiredHash); // true = immediate, desiredHash = hash to set
                return;
            }
            
            // Fallback: update hash immediately if filterController is not available
            const newUrl = `${window.location.pathname}${window.location.search}${desiredHash}`;
            window.history.pushState(null, null, newUrl);
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[TabNavigation] Update URL hash error:', error);
            }
        }
    }
}

export { TabNavigation };

